import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import * as styles from './ResponsiveTabNav.css';

const cx = classNames.bind(styles);

/**
 * Formats a tab label with an optional count.
 * @param {string} label - The tab label text
 * @param {number|undefined} count - Optional count to display
 * @returns {string} Formatted label, e.g. "In Progress (3)"
 */
const formatTabLabel = (label, count) => {
    if (count !== undefined && count !== null) {
        return `${label} (${count})`;
    }
    return label;
};

/**
 * ResponsiveTabNav — A reusable, domain-agnostic tab navigation component.
 * Renders horizontal tabs on md+ viewports (≥768px) and a <select> dropdown on sm (<768px).
 */
const ResponsiveTabNav = ({ tabs, activeTab, onTabChange, hidden, className, ariaLabel }) => {
    if (hidden) {
        return null;
    }

    const handleDropdownChange = (e) => {
        onTabChange(e.target.value);
    };

    const handleTabClick = (value) => {
        onTabChange(value);
    };

    return (
        <nav
            className={`${cx('tabNavWrapper')}${className ? ` ${className}` : ''}`}
            aria-label={ariaLabel || 'Tab navigation'}
            data-automation-id="at-responsive-tab-nav"
        >
            {/* Horizontal tabs — md+ viewports */}
            <ul className={cx('tabList')} role="tablist">
                {tabs.map((tab) => (
                    <li
                        key={tab.value}
                        role="tab"
                        aria-selected={activeTab === tab.value}
                        className={cx('tab', { tabActive: activeTab === tab.value })}
                        onClick={() => handleTabClick(tab.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleTabClick(tab.value);
                            }
                        }}
                        tabIndex={0}
                        data-automation-id={`at-tab-${tab.value}`}
                    >
                        {formatTabLabel(tab.label, tab.count)}
                    </li>
                ))}
            </ul>

            {/* Dropdown select — sm viewport */}
            <div className={cx('dropdownWrapper')}>
                <select
                    className={cx('dropdown')}
                    value={activeTab}
                    onChange={handleDropdownChange}
                    aria-label={ariaLabel || 'Tab navigation'}
                    data-automation-id="at-tab-dropdown"
                >
                    {tabs.map((tab) => (
                        <option key={tab.value} value={tab.value}>
                            {formatTabLabel(tab.label, tab.count)}
                        </option>
                    ))}
                </select>
                <span className={cx('dropdownArrow')} aria-hidden="true">
                    &#9662;
                </span>
            </div>
        </nav>
    );
};

ResponsiveTabNav.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            count: PropTypes.number,
        })
    ).isRequired,
    activeTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
    hidden: PropTypes.bool,
    className: PropTypes.string,
    ariaLabel: PropTypes.string,
};

ResponsiveTabNav.defaultProps = {
    hidden: false,
    className: '',
    ariaLabel: 'Tab navigation',
};

export default ResponsiveTabNav;
